import React, { useEffect, useState } from 'react';
import Camera, { FACING_MODES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import {
  isFirefoxBrowser,
  isSafariBrowser,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import ImageButton from 'components/common/Buttons/ImageButton';
import PermissionsService from 'services/PermissionsService';

const t = (key) => translate('activities', key);
type Props = {
  onPhotoTaken: (file: any) => void;
  onNoCameraPrivileges: () => void;
};

enum PermissionStatus {
  REQUESTING = 'permission',
  ACCEPTED = 'accept',
  REJECTED = 'reject',
}

const CameraModal = ({ onPhotoTaken, onNoCameraPrivileges }: Props): React.ReactElement => {
  const [status, setStatus] = useState(PermissionStatus.REQUESTING);
  const classes = useStyles();

  const dataURItoBlob = (dataURI) => {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
    else byteString = unescape(dataURI.split(',')[1]);
    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mimeString });
  };

  const photoTaken = (dataUri) => {
    const blob = dataURItoBlob(dataUri);
    const file = new File([blob], 'image.png', { type: 'image/png' });
    onPhotoTaken(file);
  };

  const checkPermissions = async () => {
    if (!isSafariBrowser() && !isFirefoxBrowser()) {
      const permissions = new PermissionsService();
      const state: PermissionState = await permissions.checkPermission('camera');
      if (state === 'granted') {
        setStatus(PermissionStatus.ACCEPTED);
      } else {
        setStatus(PermissionStatus.REQUESTING);
        await permissions
          .getPermission('camera')
          .then(() => setStatus(PermissionStatus.ACCEPTED))
          .catch(() => setStatus(PermissionStatus.REJECTED));
      }
    } else {
      setStatus(PermissionStatus.ACCEPTED);
    }
  };

  const onCameraError = (err) => {
    console.error({ err });
    setStatus(
      err?.name === 'NotAllowedError' ? PermissionStatus.REQUESTING : PermissionStatus.REJECTED
    );
  };

  useEffect(() => {
    (async () => await checkPermissions())();
  }, []);

  switch (status) {
    case PermissionStatus.REQUESTING:
      return (
        <Box className={classes.messagesClass}>
          <Typography variant="h3" align="center">
            {t('cameraPermissions')}
          </Typography>
          <Box mt={2} display="flex" justifyContent="center">
            <ImageButton imageName={'rejectButton'} onClick={onNoCameraPrivileges} />
          </Box>
        </Box>
      );

    case PermissionStatus.ACCEPTED:
      return (
        <div className={classes.photoContainer}>
          <Camera
            onTakePhotoAnimationDone={photoTaken}
            onCameraError={onCameraError}
            idealFacingMode={FACING_MODES.ENVIRONMENT}
            isImageMirror={false}
            isSilentMode={true}
          />
        </div>
      );

    case PermissionStatus.REJECTED:
      return (
        <Box className={classes.messagesClass}>
          <Typography variant="h3" align="center">
            {t('cameraPermissionsRejected')}
          </Typography>
          <Box mt={2} display="flex" justifyContent="center">
            <ImageButton imageName={'rejectButton'} onClick={onNoCameraPrivileges} />
          </Box>
        </Box>
      );
  }
};
export default CameraModal;
export const CameraModalStyles = {
  boxSizing: 'border-box',
  width: '100%',
  margin: '0px',
  padding: '0px',
  maxHeight: 'none',
  background: 'transparent',
  boxShadow: 'none',
  borderRadius: '8px',
  overflow: 'hidden',
};
const useStyles = makeStyles((theme) => {
  return {
    photoContainer: {
      '&>.react-html5-camera-photo>video, &>.react-html5-camera-photo>img, &>.react-html5-camera-photo>.white-flash':
        {
          width: '100%',
          height: '100%',
          maxHeight: '100vh',
          maxWidth: '100vw',
          borderRadius: '8px',
        },
      '&>.react-html5-camera-photo': {
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        maxWidth: '100%',
      },
    },
    messagesClass: {
      marginTop: remCalc(150),
      background: '#fff',
      padding: remCalc(32),
      borderRadius: remCalc(6),
      [theme.breakpoints.down('md')]: {
        padding: remCalc(24),
      },
      [theme.breakpoints.down('xs')]: {
        padding: `${remCalc(16)} ${remCalc(8)}`,
      },
    },
  };
});
